import { z } from 'zod'

export const LEARNING_COURSE_ACHIEVEMENT_CONTRACT_VERSION = '1.0.0' as const
export const COURSE_ACHIEVEMENT_SCHEMA_VERSION = 'course-achievement-v1' as const
export const COURSE_ACHIEVEMENT_BATCH_SCHEMA_VERSION = 'course-achievement-batch-v1' as const

const requiredText = z.string().trim().min(1).max(240)
const identifier = z.string().trim().min(1).max(180)
const sha256 = z.string().trim().regex(/^(?:sha256:)?[a-f0-9]{64}$/u)
const timestamp = z.string().datetime({ offset: true })

export const courseAchievementSubjectSchema = z.object({
  provider: requiredText,
  subjectId: z.string().trim().min(1).max(320),
  issuer: z.string().trim().min(1).max(512).nullable().optional().default(null),
  audience: z.string().trim().min(1).max(512).nullable().optional().default(null),
}).strict()

export const courseAchievementFactSchema = z.object({
  schemaVersion: z.literal(COURSE_ACHIEVEMENT_SCHEMA_VERSION),
  achievementId: identifier,
  subject: courseAchievementSubjectSchema,
  courseSlug: z.string().trim().min(1).max(180),
  courseRevision: sha256,
  policyHash: sha256,
  outcome: z.enum(['completed', 'passed']),
  evidenceStandard: z.enum(['self-attested', 'server-verified']),
  completedAt: timestamp,
  evidenceIds: z.array(identifier).max(1000),
  status: z.enum(['earned', 'withdrawn']),
  correctionOf: identifier.nullable(),
  payloadHash: sha256.optional(),
}).strict()

/** The achievement receiver accepts exactly one immutable fact per request. */
export const courseAchievementBatchSchema = z.object({
  schemaVersion: z.literal(COURSE_ACHIEVEMENT_BATCH_SCHEMA_VERSION),
  achievements: z.array(courseAchievementFactSchema).length(1),
}).strict().refine(batch => batch.achievements[0]?.evidenceStandard === 'self-attested', {
  message: 'The native engine receiver accepts self-attested evidence only.',
})

export const learningCourseAchievementFactSchema = courseAchievementFactSchema
export const learningCourseAchievementBatchSchema = courseAchievementBatchSchema
export const learningCourseAchievementIngestResponseSchema = z.object({
  accepted: z.array(identifier),
  duplicate: z.array(identifier),
}).strict()

export const courseAchievementBatchResponseSchema = learningCourseAchievementIngestResponseSchema
export type LearningCourseAchievementIngestResponse = z.infer<typeof learningCourseAchievementIngestResponseSchema>
export type CourseAchievementBatchResponse = z.infer<typeof courseAchievementBatchResponseSchema>
export const learningCourseAchievementBatchRequestSchema = courseAchievementBatchSchema
export const learningCourseAchievementIngestRequestSchema = courseAchievementBatchSchema

export type CourseAchievementSubject = z.infer<typeof courseAchievementSubjectSchema>
export type CourseAchievementFact = z.infer<typeof courseAchievementFactSchema>
export type CourseAchievementBatch = z.infer<typeof courseAchievementBatchSchema>
export const CourseAchievementBatchRequestSchema = courseAchievementBatchSchema
export const CourseAchievementIngestResponseSchema = learningCourseAchievementIngestResponseSchema
export type CourseAchievementIngestResponse = z.infer<typeof CourseAchievementIngestResponseSchema>
export type LearningCourseAchievementFact = z.infer<typeof learningCourseAchievementFactSchema>
export type LearningCourseAchievementBatch = z.infer<typeof learningCourseAchievementBatchSchema>
export type LearningCourseAchievementBatchRequest = z.infer<typeof learningCourseAchievementBatchRequestSchema>
export type LearningCourseAchievementIngestRequest = z.infer<typeof learningCourseAchievementIngestRequestSchema>

// Mac's application contracts use these names; keep the package boundary easy to adopt
// while the learning worker moves its handlers onto generated families.
export const CourseAchievementSubjectSchema = courseAchievementSubjectSchema
export const CourseAchievementFactSchema = courseAchievementFactSchema
export const CourseAchievementBatchSchema = courseAchievementBatchSchema
